/** Word/sentence-level text compare — ignores trivial whitespace differences. */

export type DiffSeg = { kind: "equal" | "insert" | "delete"; text: string };

/** Collapse runs of whitespace to a single space for comparison keys. */
function normKey(token: string): string {
  if (/^\s+$/.test(token)) return " ";
  return token.replace(/\s+/g, " ").trim();
}

function isWhitespaceToken(token: string): boolean {
  return /^\s+$/.test(token);
}

/**
 * Tokenize into words (Persian/Latin) and significant punctuation,
 * keeping whitespace as separate tokens so layout can be restored.
 */
function tokenize(text: string): string[] {
  if (!text) return [];
  return (
    text.match(/[\u0600-\u06FFa-zA-Z0-9_]+|[^\u0600-\u06FFa-zA-Z0-9_\s]+|\s+/g) ?? [
      text,
    ]
  );
}

/**
 * Proper LCS word-level diff. Whitespace-only tokens that match after
 * normalization are treated as equal so identical prose isn't marked TC.
 */
export function diffTexts(base: string, changed: string): DiffSeg[] {
  const a = tokenize(base);
  const b = tokenize(changed);
  const n = a.length;
  const m = b.length;
  if (!n && !m) return [];
  if (!n) return [{ kind: "insert", text: changed }];
  if (!m) return [{ kind: "delete", text: base }];

  if (n * m > 250_000) {
    return coarseSentenceDiff(base, changed);
  }

  const aKey = a.map(normKey);
  const bKey = b.map(normKey);

  const dp: Uint16Array[] = Array.from(
    { length: n + 1 },
    () => new Uint16Array(m + 1)
  );
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      dp[i]![j] =
        aKey[i - 1] === bKey[j - 1]
          ? (dp[i - 1]![j - 1]! + 1)
          : Math.max(dp[i - 1]![j]!, dp[i]![j - 1]!);
    }
  }

  const stack: DiffSeg[] = [];
  let i = n;
  let j = m;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && aKey[i - 1] === bKey[j - 1]) {
      // Prefer the "changed" side's whitespace when equal after norm
      const text =
        isWhitespaceToken(a[i - 1]!) && isWhitespaceToken(b[j - 1]!)
          ? b[j - 1]!
          : aKey[i - 1] === a[i - 1]
            ? a[i - 1]!
            : b[j - 1]!;
      // For non-ws equal keys, keep changed text if only spacing differed
      const equalText =
        aKey[i - 1] === bKey[j - 1] && a[i - 1] !== b[j - 1]
          ? isWhitespaceToken(a[i - 1]!) || isWhitespaceToken(b[j - 1]!)
            ? b[j - 1]!
            : b[j - 1]!
          : text;
      stack.push({ kind: "equal", text: equalText });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i]![j - 1]! >= dp[i - 1]![j]!)) {
      // Skip pure whitespace inserts when neighbors already absorb spacing
      if (!(isWhitespaceToken(b[j - 1]!) && wouldSkipWs(stack, "insert"))) {
        stack.push({ kind: "insert", text: b[j - 1]! });
      }
      j--;
    } else {
      if (!(isWhitespaceToken(a[i - 1]!) && wouldSkipWs(stack, "delete"))) {
        stack.push({ kind: "delete", text: a[i - 1]! });
      }
      i--;
    }
  }
  stack.reverse();
  return mergeSegs(collapseTrivialWs(stack));
}

/** Drop leading/trailing ws-only insert/delete that don't change meaning. */
function wouldSkipWs(stack: DiffSeg[], _kind: "insert" | "delete"): boolean {
  const last = stack[stack.length - 1];
  return !last || last.kind === "equal";
}

function collapseTrivialWs(segs: DiffSeg[]): DiffSeg[] {
  const out: DiffSeg[] = [];
  for (let i = 0; i < segs.length; i++) {
    const s = segs[i]!;
    if (
      (s.kind === "insert" || s.kind === "delete") &&
      isWhitespaceToken(s.text)
    ) {
      const prev = out[out.length - 1];
      const next = segs[i + 1];
      // Whitespace between two equals → absorb into equal
      if (prev?.kind === "equal" && next?.kind === "equal") {
        prev.text += s.kind === "insert" ? s.text : " ";
        continue;
      }
      // Orphan whitespace change with no word neighbor → ignore
      if (!prev || prev.kind === "equal") {
        if (!next || next.kind === "equal") continue;
      }
    }
    out.push({ ...s });
  }
  return out;
}

function splitSentences(text: string): string[] {
  const parts = text.split(/(?<=[.!?؟۔\n])\s+/);
  return parts.filter((p) => p.length > 0);
}

function coarseSentenceDiff(base: string, changed: string): DiffSeg[] {
  const a = splitSentences(base);
  const b = splitSentences(changed);
  const n = a.length;
  const m = b.length;
  if (!n) return [{ kind: "insert", text: changed }];
  if (!m) return [{ kind: "delete", text: base }];

  const aKey = a.map((s) => s.replace(/\s+/g, " ").trim());
  const bKey = b.map((s) => s.replace(/\s+/g, " ").trim());
  const dp: Uint16Array[] = Array.from(
    { length: n + 1 },
    () => new Uint16Array(m + 1)
  );
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      dp[i]![j] =
        aKey[i - 1] === bKey[j - 1]
          ? (dp[i - 1]![j - 1]! + 1)
          : Math.max(dp[i - 1]![j]!, dp[i]![j - 1]!);
    }
  }
  const stack: DiffSeg[] = [];
  let i = n;
  let j = m;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && aKey[i - 1] === bKey[j - 1]) {
      stack.push({ kind: "equal", text: b[j - 1]! + " " });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i]![j - 1]! >= dp[i - 1]![j]!)) {
      stack.push({ kind: "insert", text: b[j - 1]! + " " });
      j--;
    } else {
      stack.push({ kind: "delete", text: a[i - 1]! + " " });
      i--;
    }
  }
  stack.reverse();
  return mergeSegs(stack);
}

function mergeSegs(segs: DiffSeg[]): DiffSeg[] {
  const out: DiffSeg[] = [];
  for (const s of segs) {
    if (!s.text) continue;
    const last = out[out.length - 1];
    if (last && last.kind === s.kind) last.text += s.text;
    else out.push({ ...s });
  }
  return out;
}

/** Text and attribute values (author labels are file names — may contain quotes). */
function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Build reader HTML with TC-like marks + a synthetic comment per change block. */
export function diffToReaderHtml(
  base: string,
  changed: string,
  opts?: { baseAuthor?: string; changedAuthor?: string }
): { html: string; commentBodies: string[] } {
  const segs = diffTexts(base, changed);
  const baseAuthor = opts?.baseAuthor || "نسخه پایه";
  const changedAuthor = opts?.changedAuthor || "نسخه جدید";
  const parts: string[] = [];
  const comments: string[] = [];
  let para = "";

  const flushPara = () => {
    if (!para.trim()) {
      para = "";
      return;
    }
    parts.push(`<p class="nr-p">${para}</p>`);
    para = "";
  };

  for (const seg of segs) {
    if (seg.text.includes("\n")) {
      const lines = seg.text.split("\n");
      for (let li = 0; li < lines.length; li++) {
        const line = lines[li]!;
        if (line) {
          if (seg.kind === "insert") {
            para += `<ins class="nr-ins" data-author="${esc(changedAuthor)}" title="درج — ${esc(changedAuthor)}">${esc(line)}</ins>`;
            if (line.trim()) {
              comments.push(`درج توسط ${changedAuthor}: ${line.slice(0, 120)}`);
            }
          } else if (seg.kind === "delete") {
            para += `<del class="nr-del" data-author="${esc(baseAuthor)}" title="حذف — ${esc(baseAuthor)}">${esc(line)}</del>`;
            if (line.trim()) {
              comments.push(
                `حذف نسبت به پایه (${baseAuthor}): ${line.slice(0, 120)}`
              );
            }
          } else {
            para += esc(line);
          }
        }
        if (li < lines.length - 1) flushPara();
      }
    } else if (seg.kind === "insert") {
      if (seg.text.trim()) {
        para += `<ins class="nr-ins" data-author="${esc(changedAuthor)}" title="درج — ${esc(changedAuthor)}">${esc(seg.text)}</ins>`;
      } else {
        para += esc(seg.text);
      }
    } else if (seg.kind === "delete") {
      if (seg.text.trim()) {
        para += `<del class="nr-del" data-author="${esc(baseAuthor)}" title="حذف — ${esc(baseAuthor)}">${esc(seg.text)}</del>`;
      }
    } else {
      para += esc(seg.text);
    }
  }
  flushPara();
  if (!parts.length) parts.push('<p class="nr-p"></p>');

  return {
    html: `<article class="nr-docx" dir="auto">${parts.join("")}</article>`,
    commentBodies: comments.slice(0, 40),
  };
}
